"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Wallet, Search, Rocket, Coins, LucideIcon, CheckCircle2 } from "lucide-react"
import { motion, Variants } from "framer-motion"
import { useState } from "react"

const steps: { icon: LucideIcon; title: string; description: string; details: string[] }[] = [
    {
        icon: Wallet,
        title: "Connect Wallet",
        description: "Connect your Web3 wallet to get started with TokenKickstarter",
        details: ["MetaMask", "WalletConnect", "Coinbase Wallet", "Trust Wallet"]
    },
    {
        icon: Search,
        title: "Browse Projects",
        description: "Explore vetted token presales and review project details",
        details: ["KYC Verified", "Audited Contracts", "Transparent Terms", "Multi-Chain"]
    },
    {
        icon: Rocket,
        title: "Participate",
        description: "Invest in presales with transparent terms and smart contracts",
        details: ["Multiple Currencies", "Real-time Pricing", "Referral Bonus", "Low Fees"]
    },
    {
        icon: Coins,
        title: "Claim Tokens",
        description: "Receive your tokens after presale ends and listing occurs",
        details: ["Multi-Chain Claim", "Auto Distribution", "Vesting Options", "LP Locked"]
    },
]

// Simplified animation variants — fewer properties, shorter durations
const cardVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: "easeOut" }
    }
}

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.1,
        }
    }
}

function AnimatedStep({
    step,
    index,
    isLast
}: {
    step: typeof steps[0]
    index: number
    isLast: boolean
}) {
    const [isHovered, setIsHovered] = useState(false)
    const Icon = step.icon

    return (
        <div className="relative">
            {/* Connector Line — CSS only, no motion */}
            {!isLast && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary/50 to-primary/20 z-10" />
            )}

            <motion.div
                variants={cardVariants}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
            >
                <Card className="relative overflow-hidden border-border/40 dark:border-transparent group hover:border-primary/50 transition-colors duration-300 h-full">
                    {/* Hover gradient — CSS transition instead of motion */}
                    <div
                        className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 transition-opacity duration-300"
                        style={{ opacity: isHovered ? 1 : 0 }}
                    />

                    <CardContent className="p-6 relative z-10">
                        {/* Step Number — CSS only */}
                        <div
                            className="absolute -right-2 -top-2 text-8xl font-bold text-muted pointer-events-none select-none transition-opacity duration-300"
                            style={{ opacity: 0.1 }}
                        >
                            {index + 1}
                        </div>

                        <div className="relative">
                            {/* Icon Container — simple CSS transition */}
                            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
                                <Icon className="h-10 w-10 text-primary transition-transform duration-300 group-hover:scale-110" />
                            </div>

                            {/* Title */}
                            <div className="relative mb-2">
                                <h3 className="text-xl font-bold">{step.title}</h3>
                                <div
                                    className="h-0.5 bg-gradient-to-r from-primary to-transparent mt-1 transition-transform duration-300 origin-left"
                                    style={{ transform: `scaleX(${isHovered ? 1 : 0})` }}
                                />
                            </div>

                            {/* Description */}
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                                {step.description}
                            </p>

                            {/* Expandable Details — CSS transition for height */}
                            <div
                                className="overflow-hidden transition-all duration-300"
                                style={{
                                    maxHeight: isHovered ? '200px' : '0px',
                                    opacity: isHovered ? 1 : 0
                                }}
                            >
                                <div className="pt-3 border-t border-border/40 space-y-2">
                                    {step.details.map((detail, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-2 text-xs text-muted-foreground"
                                        >
                                            <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                            <span>{detail}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>

                    {/* Bottom Glow — CSS transition */}
                    <div
                        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent transition-all duration-300"
                        style={{
                            transform: `scaleX(${isHovered ? 1 : 0})`,
                            opacity: isHovered ? 1 : 0
                        }}
                    />
                </Card>
            </motion.div>
        </div>
    )
}

export function HowItWorks() {
    return (
        <section className="py-20 md:py-28 relative overflow-hidden">
            {/* Background glow — static, no animation */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-3xl" />
            </div>

            <div className="container relative z-10">
                {/* Header */}
                <div className="mb-16 text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                        <Rocket className="h-4 w-4" />
                        Getting Started
                    </div>
                    <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
                        How It{" "}
                        <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                            Works
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground text-balance max-w-2xl mx-auto">
                        Participate in token presales in four simple steps. Hover over each step to learn more.
                    </p>
                </div>

                {/* Progress Indicator — CSS only, no motion */}
                <div className="hidden lg:flex justify-center mb-12">
                    <div className="flex items-center gap-2">
                        {steps.map((_, index) => (
                            <div key={index} className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center text-primary font-bold">
                                    {index + 1}
                                </div>
                                {index < steps.length - 1 && (
                                    <div className="w-16 h-0.5 bg-gradient-to-r from-primary to-primary/30" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Steps Grid — single stagger container */}
                <motion.div
                    className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    {steps.map((step, index) => (
                        <AnimatedStep
                            key={step.title}
                            step={step}
                            index={index}
                            isLast={index === steps.length - 1}
                        />
                    ))}
                </motion.div>
            </div>
        </section>
    )
}
